import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "interview-list" }
const _hoisted_2 = {
  key: 0,
  class: "loading-container"
}
const _hoisted_3 = {
  key: 1,
  class: "empty-container"
}
const _hoisted_4 = { key: 2 }
const _hoisted_5 = { class: "interview-header" }
const _hoisted_6 = { class: "section-title" }
const _hoisted_7 = { class: "card-header" }
const _hoisted_8 = { class: "stage-info" }
const _hoisted_9 = { class: "stage-name" }
const _hoisted_10 = { class: "card-actions" }
const _hoisted_11 = ["onClick"]
const _hoisted_12 = ["onClick"]
const _hoisted_13 = ["onClick"]
const _hoisted_14 = { class: "interview-details" }
const _hoisted_15 = {
  key: 0,
  class: "detail-item"
}
const _hoisted_16 = {
  key: 1,
  class: "detail-item"
}
const _hoisted_17 = {
  key: 2,
  class: "detail-item"
}
const _hoisted_18 = {
  key: 3,
  class: "detail-item notes"
}
const _hoisted_19 = {
  key: 4,
  class: "detail-item"
}
const _hoisted_20 = {
  key: 5,
  class: "detail-item feedback"
}
const _hoisted_21 = { class: "dialog-footer" }

import { ref, computed, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Edit, Delete, User, Location, Timer, Memo, Document, ChatDotRound, Check } from '@element-plus/icons-vue';
import type { Interview, UpdateInterview } from '@/types/interview';
import type { ApiResponse, PageResponse } from '@/types/common';
import { INTERVIEW_STAGES, INTERVIEW_STATUS } from '@/types/interview';
import interviewService from '@/services/interviewService';
import InterviewForm from './InterviewForm.vue';
import dayjs from 'dayjs';


export default /*@__PURE__*/_defineComponent({
  __name: 'InterviewList',
  props: {
    positionId: {}
  },
  emits: ["refresh"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const loading = ref(false);
const interviews = ref<Interview[]>([]);
const dialogVisible = ref(false);
const isEditing = ref(false);
const currentInterview = ref<UpdateInterview | undefined>(undefined);

// 按面试时间排序的面试列表
const sortedInterviews = computed(() => {
  return [...interviews.value].sort((a, b) => {
    return dayjs(a.dateTime).isAfter(dayjs(b.dateTime)) ? 1 : -1;
  });
});

// 页面加载时获取面试列表
onMounted(async () => {
  console.log('InterviewList组件加载，职位ID:', props.positionId);
  await fetchInterviews();
});

// 获取面试列表
const fetchInterviews = async () => {
  loading.value = true;
  try {
    const response = await interviewService.getInterviewsByPosition(props.positionId);
    console.log('API Response:', response); // 调试日志
    
    if (response && response.content) {
      interviews.value = response.content;
      console.log('Interviews loaded:', interviews.value); // 调试日志
    } else {
      console.warn('Invalid response format:', response); // 调试日志
      interviews.value = [];
    }
  } catch (error) {
    console.error('Failed to fetch interviews:', error);
    ElMessage.error('获取面试列表失败');
    interviews.value = [];
  } finally {
    loading.value = false;
  }
};

// 添加面试
const addInterview = () => {
  isEditing.value = false;
  currentInterview.value = undefined;
  dialogVisible.value = true;
};

// 编辑面试
const editInterview = (interview: Interview) => {
  // 创建编辑对象，只包含更新所需字段
  const editingInterview: UpdateInterview = {
    id: interview.id!,
    stage: interview.stage,
    dateTime: interview.dateTime,
    duration: interview.duration,
    location: interview.location,
    notes: interview.notes,
    feedback: interview.feedback,
    preparationMaterials: interview.preparationMaterials,
    status: interview.status,
    interviewer: interview.interviewer
  };
  
  isEditing.value = true;
  currentInterview.value = editingInterview;
  dialogVisible.value = true;
};

// 确认删除
const confirmDelete = (interview: Interview) => {
  ElMessageBox.confirm(
    `确定要删除此面试记录吗？${interview.stage ? `(${getStageLabel(interview.stage)})` : ''}`,
    '删除确认',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  )
    .then(async () => {
      if (interview.id) {
        await deleteInterview(interview.id);
      }
    })
    .catch(() => {
      // 用户取消删除
    });
};

// 删除面试
const deleteInterview = async (id: number) => {
  try {
    const response = await interviewService.deleteInterview(id);
    ElMessage.success('面试删除成功');
    await fetchInterviews();
    emit('refresh');
  } catch (error) {
    console.error('删除面试失败:', error);
    ElMessage.error('删除面试失败');
  }
};

// 处理创建面试成功
const handleCreated = async (interview: Interview) => {
  // 刷新面试列表以获取最新数据
  await fetchInterviews();
  dialogVisible.value = false;
  emit('refresh');
};

// 处理更新面试成功
const handleUpdated = async (interview: Interview) => {
  // 刷新面试列表以获取最新数据
  await fetchInterviews();
  dialogVisible.value = false;
  emit('refresh');
};

// 格式化日期时间
const formatDateTime = (dateTime: string) => {
  if (!dateTime) return '';
  return dayjs(dateTime).format('YYYY-MM-DD HH:mm');
};

// 获取面试阶段标签
const getStageLabel = (stage: string) => {
  return INTERVIEW_STAGES[stage as keyof typeof INTERVIEW_STAGES] || stage;
};

// 获取面试状态标签
const getStatusLabel = (status: string | null | undefined) => {
  if (!status) return '未设置';
  return INTERVIEW_STATUS[status as keyof typeof INTERVIEW_STATUS] || status;
};

// 获取状态对应的类型
const getStatusType = (status: string | null | undefined) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'danger';
    case 'pending':
      return 'warning';
    default:
      return 'info';
  }
};

// 获取时间轴项目类型
const getTimelineItemType = (interview: Interview) => {
  switch (interview.status) {
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'danger';
    case 'pending':
      return interview.dateTime && dayjs(interview.dateTime).isBefore(dayjs()) ? 'warning' : 'primary';
    default:
      return 'info';
  }
};

// 修改完成面试的方法
const completeInterview = async (interview: Interview) => {
  try {
    const response = await interviewService.updateInterview({
      id: interview.id!,
      status: 'completed',
      stage: interview.stage,
      dateTime: interview.dateTime
    });
    
    ElMessage.success('面试已标记为完成');
    await fetchInterviews();
    emit('refresh');
  } catch (error) {
    console.error('更新面试状态失败:', error);
    ElMessage.error('操作失败，请重试');
  }
};

return (_ctx: any,_cache: any) => {
  const _component_el_skeleton = _resolveComponent("el-skeleton")!
  const _component_el_empty = _resolveComponent("el-empty")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_card = _resolveComponent("el-card")!
  const _component_el_timeline_item = _resolveComponent("el-timeline-item")!
  const _component_el_timeline = _resolveComponent("el-timeline")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_component_el_skeleton, {
            rows: 3,
            animated: ""
          })
        ]))
      : (interviews.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_component_el_empty, { description: "暂无面试记录" }),
            _createVNode(_component_el_button, {
              type: "primary",
              onClick: addInterview
            }, {
              default: _withCtx(() => _cache[3] || (_cache[3] = [
                _createTextVNode("添加面试")
              ])),
              _: 1
            })
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("h3", _hoisted_6, "面试记录 (" + _toDisplayString(interviews.value.length) + ")", 1),
              _createVNode(_component_el_button, {
                type: "primary",
                size: "small",
                onClick: addInterview
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_icon, null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Plus))
                    ]),
                    _: 1
                  }),
                  _cache[4] || (_cache[4] = _createTextVNode(" 添加面试 "))
                ]),
                _: 1
              })
            ]),
            _createVNode(_component_el_timeline, { class: "interview-timeline" }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedInterviews.value, (interview) => {
                  return (_openBlock(), _createBlock(_component_el_timeline_item, {
                    key: interview.id,
                    timestamp: formatDateTime(interview.dateTime),
                    type: getTimelineItemType(interview),
                    size: "large",
                    hollow: interview.status === 'completed'
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_card, { class: "interview-card" }, {
                        header: _withCtx(() => [
                          _createElementVNode("div", _hoisted_7, [
                            _createElementVNode("div", _hoisted_8, [
                              _createElementVNode("span", _hoisted_9, _toDisplayString(getStageLabel(interview.stage)), 1),
                              _createVNode(_component_el_tag, {
                                size: "small",
                                type: getStatusType(interview.status)
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(getStatusLabel(interview.status)), 1)
                                ]),
                                _: 2
                              }, 1032, ["type"])
                            ]),
                            _createElementVNode("div", _hoisted_10, [
                              (interview.status !== 'completed')
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 0,
                                    class: "icon-item",
                                    onClick: ($event: any) => (completeInterview(interview))
                                  }, [
                                    _createVNode(_component_el_icon, { class: "custom-icon complete-icon" }, {
                                      default: _withCtx(() => [
                                        _createVNode(_unref(Check))
                                      ]),
                                      _: 1
                                    }),
                                    _cache[5] || (_cache[5] = _createElementVNode("span", { class: "icon-label" }, "完成", -1))
                                  ], 8, _hoisted_11))
                                : _createCommentVNode("", true),
                              _createElementVNode("div", {
                                class: "icon-item",
                                onClick: ($event: any) => (editInterview(interview))
                              }, [
                                _createVNode(_component_el_icon, { class: "custom-icon edit-icon" }, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(Edit))
                                  ]),
                                  _: 1
                                }),
                                _cache[6] || (_cache[6] = _createElementVNode("span", { class: "icon-label" }, "编辑", -1))
                              ], 8, _hoisted_12),
                              _createElementVNode("div", {
                                class: "icon-item",
                                onClick: ($event: any) => (confirmDelete(interview))
                              }, [
                                _createVNode(_component_el_icon, { class: "custom-icon delete-icon" }, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(Delete))
                                  ]),
                                  _: 1
                                }),
                                _cache[7] || (_cache[7] = _createElementVNode("span", { class: "icon-label" }, "删除", -1))
                              ], 8, _hoisted_13)
                            ])
                          ])
                        ]),
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_14, [
                            (interview.interviewer)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                                  _createVNode(_component_el_icon, null, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(User))
                                    ]),
                                    _: 1
                                  }),
                                  _createElementVNode("span", null, "面试官: " + _toDisplayString(interview.interviewer), 1)
                                ]))
                              : _createCommentVNode("", true),
                            (interview.location)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                  _createVNode(_component_el_icon, null, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(Location))
                                    ]),
                                    _: 1
                                  }),
                                  _createElementVNode("span", null, "地点: " + _toDisplayString(interview.location), 1)
                                ]))
                              : _createCommentVNode("", true),
                            (interview.duration)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                  _createVNode(_component_el_icon, null, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(Timer))
                                    ]),
                                    _: 1
                                  }),
                                  _createElementVNode("span", null, "时长: " + _toDisplayString(interview.duration) + " 分钟", 1)
                                ]))
                              : _createCommentVNode("", true),
                            (interview.notes)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                                  _createVNode(_component_el_icon, null, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(Memo))
                                    ]),
                                    _: 1
                                  }),
                                  _createElementVNode("span", null, "备注: " + _toDisplayString(interview.notes), 1)
                                ]))
                              : _createCommentVNode("", true),
                            (interview.preparationMaterials)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                  _createVNode(_component_el_icon, null, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(Document))
                                    ]),
                                    _: 1
                                  }),
                                  _createElementVNode("span", null, "准备材料: " + _toDisplayString(interview.preparationMaterials), 1)
                                ]))
                              : _createCommentVNode("", true),
                            (interview.feedback)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                                  _createVNode(_component_el_icon, null, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(ChatDotRound))
                                    ]),
                                    _: 1
                                  }),
                                  _createElementVNode("span", null, "反馈: " + _toDisplayString(interview.feedback), 1)
                                ]))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 2
                  }, 1032, ["timestamp", "type", "hollow"]))
                }), 128))
              ]),
              _: 1
            })
          ])),
    _createVNode(_component_el_dialog, {
      modelValue: dialogVisible.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((dialogVisible).value = $event)),
      title: isEditing.value ? '编辑面试' : '添加面试',
      width: "650px",
      "destroy-on-close": ""
    }, {
      footer: _withCtx(() => [
        _createElementVNode("span", _hoisted_21, [
          _createVNode(_component_el_button, {
            onClick: _cache[1] || (_cache[1] = ($event: any) => (dialogVisible.value = false))
          }, {
            default: _withCtx(() => _cache[8] || (_cache[8] = [
              _createTextVNode("取消")
            ])),
            _: 1
          })
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(InterviewForm, {
          "position-id": _ctx.positionId,
          interview: currentInterview.value,
          editing: isEditing.value,
          onCreated: handleCreated,
          onUpdated: handleUpdated,
          onCancel: _cache[0] || (_cache[0] = ($event: any) => (dialogVisible.value = false))
        }, null, 8, ["position-id", "interview", "editing"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ]))
}
}

})